import type { ServiceRequestStatus } from '../../types/serviceRequestListType';
import type { ServiceRequestRole } from '../../types/viewServiceRequestType';
import {
  TECHNICIAN_MISSED_VISIT_REASONS,
  VIEW_SERVICE_REQUEST_LABELS as L,
} from '../../constants/labels';

/**
 * Map a stored missed-visit reason code (RR-622) to its display label. The BE
 * persists the language-agnostic code (e.g. "client_not_available"); the FE
 * owns the label. Returns `''` for an unknown / absent code so callers can skip
 * rendering the reason line.
 */
export function getMissedVisitReasonLabel(
  reason: string | null | undefined
): string {
  if (!reason) {
    return '';
  }
  return (
    TECHNICIAN_MISSED_VISIT_REASONS.find((r) => r.id === reason)?.label ?? ''
  );
}

export const STATUS_CONFIG: Record<
  ServiceRequestStatus,
  { label: string; bg: string; text: string }
> = {
  pending: {
    label: 'Oczekuje',
    bg: '#FBF4D6',
    text: '#E6B800',
  },
  in_review: {
    label: 'W trakcie weryfikacji',
    bg: '#EAF3FF',
    text: '#1E7CE8',
  },
  assigned: { label: 'Przydzielone', bg: '#E8DDFF', text: '#63597C' },
  accepted: { label: 'Zaakceptowane', bg: '#E6F6F0', text: '#00AB76' },
  rejected: { label: 'Odrzucone', bg: '#FDE8E8', text: '#E11B1B' },
  in_progress: { label: 'W realizacji', bg: '#FFF6E5', text: '#FF9800' },
  delayed: {
    label: 'Opóźnione',
    bg: '#FBF4D6',
    text: '#E6B800',
  },
  completed: { label: 'Zakończone', bg: '#E6F6F0', text: '#00AB76' },
  closed: { label: 'Zamknięte', bg: '#E8EEF0', text: '#121F28' },
  resolved: { label: 'Rozwiązane', bg: '#E6F6F0', text: '#00AB76' },
  cancelled: { label: 'Anulowane', bg: '#FDE8E8', text: '#E11B1B' },
  unfulfilled: { label: 'Niezrealizowane', bg: '#E8EEF0', text: '#121F28' },
};

/**
 * FE-mirrored copy of the backend status-transition map. Backend is the
 * authoritative gate (see context/update-service-request.md); this only
 * filters the dropdown so admins don't see options the API would 400 on.
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<
  ServiceRequestStatus,
  ServiceRequestStatus[]
> = {
  pending: ['in_review', 'closed', 'cancelled'],
  in_review: ['closed', 'cancelled'],
  assigned: ['closed', 'cancelled'],
  accepted: ['closed', 'cancelled'],
  rejected: ['closed', 'cancelled'],
  completed: ['closed'],
  closed: [],
  cancelled: [],
  in_progress: ['closed', 'cancelled'],
  delayed: ['closed', 'cancelled'],
  resolved: [],
  unfulfilled: ['closed', 'cancelled'],
};

export const getAllowedNextStatuses = (
  current: ServiceRequestStatus
): ServiceRequestStatus[] => ALLOWED_STATUS_TRANSITIONS[current] ?? [];

/**
 * FE mirror of the backend technician transition map
 * (`TechnicianStatusTransitionService`, RR-624). Technicians advance a task
 * assigned to them through the same `PATCH /service-requests/{id}/status`
 * endpoint, but with their own, narrower set of allowed targets:
 *
 * - `accepted`    → `in_progress`
 * - `in_progress` → `unfulfilled`
 *
 * The backend also models `in_progress → completed`, but `completed` is driven
 * by the form-submission flow (not this endpoint), so it is intentionally
 * omitted here. Backend remains the authoritative gate; this only filters the
 * dropdown so technicians don't see options the API would reject.
 */
export const TECHNICIAN_ALLOWED_STATUS_TRANSITIONS: Partial<
  Record<ServiceRequestStatus, ServiceRequestStatus[]>
> = {
  accepted: ['in_progress'],
  in_progress: ['unfulfilled'],
};

export const getTechnicianAllowedNextStatuses = (
  current: ServiceRequestStatus
): ServiceRequestStatus[] =>
  TECHNICIAN_ALLOWED_STATUS_TRANSITIONS[current] ?? [];

/**
 * FE mirror of the backend admin-*assignee* transition map (RR-916). An admin
 * who is the assignee of a task — reached via the "Moje zadania" view — manages
 * it the same way a technician does, plus may cancel from `accepted`:
 *
 * - `accepted`    → `in_progress` (start the work) and `cancelled`
 * - `in_progress` → `unfulfilled` (missed visit)
 *
 * As with the technician map, `completed` (form-submission flow) and `delayed`
 * (auto-delay cron) are intentionally omitted — they are never set via this
 * manual PATCH. Two time-based gates are layered on top of this map at the call
 * site (see {@link useUpdateServiceRequestStatus}):
 * - `accepted → in_progress` is offered only once the scheduled start passes.
 * - `accepted → cancelled` is hidden within 24h of the scheduled visit.
 *
 * The backend remains the authoritative gate; this only filters the dropdown so
 * the admin doesn't see options the API would reject.
 */
export const ADMIN_ASSIGNEE_ALLOWED_STATUS_TRANSITIONS: Partial<
  Record<ServiceRequestStatus, ServiceRequestStatus[]>
> = {
  accepted: ['in_progress', 'cancelled'],
  in_progress: ['unfulfilled'],
};

export const getAdminAssigneeAllowedNextStatuses = (
  current: ServiceRequestStatus
): ServiceRequestStatus[] =>
  ADMIN_ASSIGNEE_ALLOWED_STATUS_TRANSITIONS[current] ?? [];

export function formatSubmittedOn(iso: string | null | undefined): string {
  if (!iso) {
    return L.EMPTY_CELL;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return L.EMPTY_CELL;
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}/${month}/${day}, ${hours}:${minutes}:${seconds}`;
}

export function orDash(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return L.EMPTY_CELL;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? L.EMPTY_CELL : trimmed;
}

const WARSAW_TZ = 'Europe/Warsaw';

function getWarsawParts(
  iso: string
): Record<'year' | 'month' | 'day' | 'hour' | 'minute', string> | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: WARSAW_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const lookup: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') {
      lookup[p.type] = p.value;
    }
  }
  // `hour: '2-digit'` with hour12: false can yield "24" at midnight on some
  // engines; normalize to "00".
  if (lookup['hour'] === '24') {
    lookup['hour'] = '00';
  }
  return lookup as Record<'year' | 'month' | 'day' | 'hour' | 'minute', string>;
}

/**
 * Decompose a UTC ISO timestamp into Warsaw wall-clock parts: a `Date`
 * constructed from the Warsaw Y/M/D (at midnight local) and an `HH:mm`
 * string. Use this when prefilling pickers that expect "local" values but
 * the underlying timestamp is server-side UTC.
 *
 * Returns `null` for malformed input.
 */
export function parseWarsawIso(
  iso: string | null | undefined
): { date: Date; time: string } | null {
  if (!iso) {
    return null;
  }
  const parts = getWarsawParts(iso);
  if (!parts) {
    return null;
  }
  const date = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day)
  );
  return { date, time: `${parts.hour}:${parts.minute}` };
}

export function formatScheduledDate(iso: string | null | undefined): string {
  if (!iso) {
    return L.EMPTY_CELL;
  }
  const parts = getWarsawParts(iso);
  if (!parts) {
    return L.EMPTY_CELL;
  }
  return `${parts.year}/${parts.month}/${parts.day}`;
}

export function formatScheduledTime(iso: string | null | undefined): string {
  if (!iso) {
    return L.EMPTY_CELL;
  }
  const parts = getWarsawParts(iso);
  if (!parts) {
    return L.EMPTY_CELL;
  }
  return `${parts.hour}:${parts.minute}`;
}

/**
 * Compose the facility address parts into a single human-readable location
 * string (RR-622, shown on the technician "Unfulfilled Visit" step). Empty /
 * null parts are dropped; returns `''` when nothing is available so callers can
 * fall back to the read-only "—" placeholder.
 */
export function formatFacilityLocation(
  parts: ReadonlyArray<string | null | undefined>
): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0)
    .join(', ');
}

export function formatRole(
  role: ServiceRequestRole | null | undefined
): string {
  switch (role) {
    case 'CLIENT':
      return L.ROLE_CLIENT;
    case 'ADMIN':
      return L.ROLE_ADMIN;
    case 'SUPER_ADMIN':
      return L.ROLE_SUPER_ADMIN;
    case 'TECHNICIAN':
      return L.ROLE_TECHNICIAN;
    default:
      return L.EMPTY_CELL;
  }
}
